import { subText } from "@/components/table";
import { type DirectoryLookup, useDirectoryLookup } from "@/lib/api";

/**
 * Who the typed UPN actually is, shown under the field as HR types it.
 *
 * The field used to be free text that nothing checked until the worker ran, so
 * a typo was invisible for days and a plausible-but-wrong name was invisible
 * for good. This is the cheapest possible fix: one line under the input naming
 * the human being who is about to lose their access.
 *
 * Four states, and they are deliberately different sentences:
 *
 * * **nothing typed yet / still typing** — silent. Help that appears on every
 *   keystroke is noise.
 * * **found** — name, job title, department, and the facts that change what
 *   offboarding means for them: already disabled, a guest, cloud-only.
 * * **not found** — said plainly. This is the typo case, and it is the entire
 *   point of the feature.
 * * **could not look** — said as ITS OWN state, never as "not found". Telling
 *   HR a real colleague does not exist, because Graph was briefly unreachable,
 *   would have them retyping a name that was right the first time.
 *
 * Blockers are a preview of the worker's hard denials, not the decision: the
 * worker re-checks every one at execution. Saying so here stops the preview
 * from being read as a guarantee.
 */
export function SubjectLookup({ upn }: { upn: string }) {
  const { data, isFetching, error } = useDirectoryLookup(upn);

  if (!upn.trim()) {
    return null;
  }
  if (isFetching) {
    return <p className={`${subText} mt-1`}>Checking the directory…</p>;
  }
  if (error) {
    return (
      <p className={`${subText} mt-1`}>
        The directory could not be read just now, so this name has not been checked. You can still
        save the record.
      </p>
    );
  }
  if (!data) {
    return null;
  }
  if (!data.found) {
    return (
      <p className="text-danger-fg mt-1 text-xs">
        No account with that sign-in name. Check the spelling: the sign-in name usually ends in
        @noricangroup.com, while their email address ends in @norican.com.
      </p>
    );
  }
  return <Found data={data} />;
}

const BLOCKER_TEXT: Record<string, string> = {
  directory_role: "holds a directory role in Entra",
  protected_admin_unit: "sits in the protected administrative unit",
  break_glass: "is a break-glass emergency account",
};

function Found({ data }: { data: DirectoryLookup }) {
  // Facts that change what an offboarding means, rather than everything the
  // directory holds. A guest cannot have sessions revoked; a cloud-only
  // account has no on-prem half; one already disabled is usually a sign that
  // the wrong person, or the right person twice, has been named.
  const notes: string[] = [];
  if (data.account_enabled === false) {
    notes.push("already disabled");
  }
  if ((data.user_type ?? "").toLowerCase() === "guest") {
    notes.push("a guest account: sessions cannot be revoked for guests");
  }
  if (data.on_premises_synced === false) {
    notes.push("cloud-only, so there is no on-prem account to disable");
  }

  return (
    <div className="mt-1">
      <p className="text-fg text-xs">
        <span className="font-medium">{data.display_name}</span>
        {data.job_title ? ` — ${data.job_title}` : ""}
        {data.department ? `, ${data.department}` : ""}
      </p>
      <p className={subText}>
        {[data.mail, data.office_location, data.manager ? `manager: ${data.manager}` : null]
          .filter(Boolean)
          .join(" · ")}
      </p>
      {notes.length > 0 ? <p className={subText}>Note: {notes.join("; ")}.</p> : null}
      {data.blockers && data.blockers.length > 0 ? (
        <p className="text-warning-fg mt-1 text-xs">
          This person {data.blockers.map((b) => BLOCKER_TEXT[b] ?? b).join(" and ")}, so HROps will
          refuse to offboard them. Raise it with SecOps instead of scheduling it here.
        </p>
      ) : null}
    </div>
  );
}
